import { useEffect, useState, useImperativeHandle, forwardRef, useCallback } from "react"
import { Form, Input } from "antd"
import { H4 } from "components/ui/typography/titles/Titles"
import { OrderedModelNumbersSelect } from "components/ui/orderedModelNumbersSelect/OrderedModelNumbersSelect"
import { ModelNumberOrderInfo } from "components/ui/modelNumberOrderInfo/ModelNumberOrderInfo"
import { Select } from "components/ui/select/Select"
import { useCategories } from "hooks/categories.hook"
import { useAddSimpleCategoryStore } from "store/simpleCategory.store"
import { AddModelObjectProvider } from "components/providers/Forms.provider"
import { useWithoutCategoryModelNumbers } from "hooks/withoutCategoryModelNumbers.hook"
import { OBJECT_LABEL_VALIDATION, OBJECT_VALIDATION_MESSAGES } from "helpers/validation/objectValidation.helper"
import type { TTopLevelCategory } from "types/drawer.type"
import type { IAddObjectMethods } from "types/configuration.type"
import style from "./SimpleObject.module.scss"

const { addModelObjectForm, block, label, description, endOfSection, startOfSection, title, select } = style

type Props = {
	objectCategoriesData: TTopLevelCategory[]
}

export const SimpleObject = forwardRef<IAddObjectMethods, Props>(({ objectCategoriesData }: Props, ref) => {
	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	const [selectedTopLevelCategory, setSelectedTopLevelCategory] = useState<string | number | undefined>()

	const { topLevelCategories, superCategories } = useCategories(objectCategoriesData, selectedTopLevelCategory)
	const { setFormValues, setModelNumbers, category, resetForm: resetStoreForm } = useAddSimpleCategoryStore()
	const { options: modelNumberOptions } = useWithoutCategoryModelNumbers()

	useEffect(() => {
		setFormValues({
			name: values?.name,
			label: values?.label,
			topLevelCategory: values?.topLevelCategory,
			superCategory: values?.superCategory,
		})
	}, [setFormValues, values])

	// Change top level category
	const onChangeTopLevelCategoryHandle = (value: string | number) => {
		setSelectedTopLevelCategory(value)
		form.resetFields(["superCategory"])
	}

	const onModelNumbersChange = useCallback(
		(selectedModelNumbers: number[]) => {
			setModelNumbers(selectedModelNumbers)
		},
		[setModelNumbers],
	)

	useImperativeHandle(ref, () => ({
		resetForm: () => {
			form.resetFields()
			resetStoreForm()
		},
	}))

	return (
		<AddModelObjectProvider>
			<Form form={form} size="large">
				<div className={addModelObjectForm}>
					<div className={block}>
						<span className={label}>Name</span>
						<Form.Item name="name" rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.NAME_REQUIRED }]}>
							<Input data-testid="name" />
						</Form.Item>
					</div>
					<div className={`${block} ${endOfSection}`}>
						<span className={label}>Label</span>
						<span className={description}>
							<span className={description}>Used in train set</span>
							<Form.Item
								name="label"
								required={true}
								rules={[
									{ required: true, message: OBJECT_VALIDATION_MESSAGES.LABEL_REQUIRED },
									OBJECT_LABEL_VALIDATION.LABEL_PREFIX,
								]}
							>
								<Input data-testid="label" />
							</Form.Item>
						</span>
					</div>
					<div className={block}>
						<H4 className={title}>Categories</H4>
						<span className={label}>Top level category</span>
						<Form.Item
							name="topLevelCategory"
							data-testid="topLevelCategorySelect"
							required={true}
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.TOP_LEVEL_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								allowClear={false}
								variant="outlined"
								options={topLevelCategories}
								onChange={onChangeTopLevelCategoryHandle}
								className={select}
							/>
						</Form.Item>
					</div>
					<div className={block}>
						<span className={label}>Super category</span>
						<Form.Item
							name="superCategory"
							required={true}
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.SUPER_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								variant="outlined"
								allowClear={false}
								className={select}
								options={superCategories}
							/>
						</Form.Item>
					</div>
					<div className={`${block} ${startOfSection}`}>
						<ModelNumberOrderInfo />
						<OrderedModelNumbersSelect
							value={category.modelNumbers}
							onChange={onModelNumbersChange}
							options={modelNumberOptions}
							data-testid="model-numbers-select"
						/>
					</div>
				</div>
			</Form>
		</AddModelObjectProvider>
	)
})
